import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { api } from '../config';
import { verify, JWT_SECRET } from '../utils/jwt';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state, action) => (handlers[action.type]
  ? handlers[action.type](state, action)
  : state);

const AuthContext = createContext({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const refresh = async () => {
    try {
      const response = await axios.post(`${api.url}/auth/refresh`, {
        refresh_token: window.localStorage.getItem('refreshToken')
      });
      const { access_token: accessToken, refresh_token: refreshToken, expires } = response.data.data;
      setSession(accessToken);
      window.localStorage.setItem('refreshToken', refreshToken);
      setTimeout(refresh, expires - 10000);
    } catch (e) {
      logout();
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const refreshToken = window.localStorage.getItem('refreshToken');

        if (accessToken && verify(accessToken, JWT_SECRET)) {
          if (refreshToken) {
            await refresh();
          } else {
            setSession(accessToken);
          }

          const response = await axios.get(`${api.url}/users/me?fields=*,role.*`);
          const user = response.data.data;

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    setSession(null);
    const response = await axios.post(`${api.url}/auth/login`, {
      email,
      password
    });
    const { expires, refresh_token: refreshToken, access_token: accessToken } = response.data.data;
    window.localStorage.setItem('refreshToken', refreshToken);
    setTimeout(refresh, expires - 10000);

    setSession(accessToken);

    const meResponse = await axios.get(`${api.url}/users/me?fields=*,role.*`);
    const user = meResponse.data.data;

    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const register = async (email, name, password) => {
    const response = await axios.post('/api/authentication/register', {
      email,
      name,
      password
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);
    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
